// src/admin/screens/loans/actions/ForceFailDisbursementModal.tsx
// Used ONLY when a loan is stuck in disbursement_pending and the aggregator
// confirms the funds never left. Flips status to disbursement_failed so the
// admin can retry or cancel. The reason is stored in cancellation_reason
// (which we use as a generic "why did state change" field until the next
// successful disbursement clears it).
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import { recordAudit } from '@admin/lib/audit';
import Modal from '@admin/components/Modal';
import Button from '@admin/components/Button';
import FormField from '@admin/components/FormField';
import { useToast } from '@admin/components/ToastProvider';
import { translateError } from '@admin/utils/translateError';
import type { LoanDetail } from '@admin/hooks/useLoan';

const schema = z.object({ reason: z.string().min(3, 'Reason required') });
type Values = z.infer<typeof schema>;

interface Props {
  loan: LoanDetail;
  onClose: (refresh: boolean) => void;
}

export default function ForceFailDisbursementModal({ loan, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Values>({ resolver: zodResolver(schema) });

  const submit = async (v: Values) => {
    setBusy(true);
    try {
      const { error } = await supabase
        .from('loans')
        .update({
          status: 'disbursement_failed',
          cancellation_reason: v.reason,
        })
        .eq('id', loan.id);
      if (error) throw error;
      await recordAudit({
        action: 'loan.force_fail_disbursement',
        target_type: 'loan',
        target_id: loan.id,
        payload: { reason: v.reason },
      });
      toast({
        kind: 'success',
        msg: `${loan.loan_number} marked as disbursement-failed`,
      });
      await qc.invalidateQueries({ queryKey: ['loan', loan.id] });
      onClose(true);
    } catch (err) {
      toast({ kind: 'error', msg: translateError(err as Error) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => onClose(false)}
      title={`Force-fail disbursement — ${loan.loan_number}`}
    >
      <form
        onSubmit={handleSubmit(submit)}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <p className="small">
          Use this only when the disbursement has been stuck in{' '}
          <code>disbursement_pending</code> and the aggregator confirms it
          never left. The loan can be retried from the failed state.
        </p>
        <FormField
          label="Reason"
          htmlFor="rsn"
          error={errors.reason?.message}
          required
        >
          <textarea id="rsn" rows={3} {...register('reason')} />
        </FormField>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onClose(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button type="submit" variant="danger" loading={busy}>
            Mark failed
          </Button>
        </div>
      </form>
    </Modal>
  );
}
